import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';
import { paginationQuerySchema, paginationMetaSchema } from './pagination.js';
import { MAIL_REMATCH_MAX_SOURCES } from '../constants/mail.js';
import { mailMessageSummarySchema } from './mail-message.js';
export const mailRecordTargetSchema = z
    .object({
    customerId: idSchema,
    contactId: idSchema.nullable().default(null),
    addressId: idSchema.nullable().default(null),
})
    .strict()
    .refine((value) => !value.contactId || !value.addressId, {
    message: 'mail.assignment.targetInvalid',
});
export const mailAssignmentSchema = z
    .object({
    id: idSchema,
    sourceId: idSchema,
    customerId: idSchema,
    contactId: idSchema.nullable(),
    addressId: idSchema.nullable(),
    partnerName: z.string(),
    contactName: z.string().nullable(),
    addressLabel: z.string().nullable(),
    state: z.enum(['LINKED', 'AMBIGUOUS', 'REMOVED']),
    manual: z.boolean(),
    bccOnly: z.boolean(),
    reasonCode: z.enum([
        'PARTNER_EMAIL',
        'CONTACT_EMAIL',
        'ADDRESS_EMAIL',
        'EXTRA_EMAIL',
        'MANUAL',
    ]),
    reason: z.string().nullable(),
    version: z.number().int().positive(),
})
    .strict();
export const mailAssignmentStateSchema = z
    .object({
    sourceId: idSchema,
    version: z.number().int().positive(),
    recordVisibility: z.enum(['PRIVATE', 'BUSINESS', 'EXCLUDED']),
    businessIrrelevant: z.boolean(),
    canEdit: z.boolean(),
    canRematch: z.boolean(),
    canAddTarget: z.boolean(),
    assignments: z.array(mailAssignmentSchema),
})
    .strict();
export const mailAssignmentMutationSchema = z
    .object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3).max(1000),
    target: mailRecordTargetSchema.optional(),
    action: z.enum(['ADD', 'REMOVE', 'RELEASE', 'PRIVATE', 'IRRELEVANT', 'RELEVANT']),
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.action === 'ADD' || value.action === 'REMOVE') !== Boolean(value.target))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target'],
            message: 'mail.assignment.targetInvalid',
        });
});
export const mailRecordQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    contactId: idSchema.optional(),
    addressId: idSchema.optional(),
});
export const mailRecordListSchema = z
    .object({
    data: z.array(mailMessageSummarySchema),
    meta: paginationMetaSchema,
})
    .strict();
export const mailEmailFieldBindingInputSchema = z
    .object({
    definitionId: idSchema,
    entityType: z.enum(['CUSTOMER', 'CONTACT', 'ADDRESS']),
    publishedRevision: z.number().int().positive(),
    version: z.number().int().nonnegative(),
    isActive: z.boolean(),
    reason: z.string().trim().min(3).max(1000),
})
    .strict();
export const mailEmailFieldBindingSchema = z
    .object({
    id: idSchema.nullable(),
    definitionId: idSchema,
    entityType: z.enum(['CUSTOMER', 'CONTACT', 'ADDRESS']),
    label: z.string(),
    publishedRevision: z.number().int().positive(),
    approvedRevision: z.number().int().positive().nullable(),
    version: z.number().int().nonnegative(),
    isActive: z.boolean(),
    current: z.boolean(),
})
    .strict();
export const mailEmailFieldBindingsSchema = z
    .object({ data: z.array(mailEmailFieldBindingSchema) })
    .strict();
export const mailRematchInputSchema = z
    .object({ sourceIds: z.array(idSchema).min(1).max(MAIL_REMATCH_MAX_SOURCES) })
    .strict();
export const mailRematchPreviewSchema = z
    .object({
    id: idSchema,
    snapshotHash: z.string().regex(/^[a-f0-9]{64}$/u),
    expiresAt: isoDateTimeSchema,
    applied: z.boolean(),
    data: z.array(z
        .object({
        sourceId: idSchema,
        linked: z.number().int().nonnegative(),
        ambiguous: z.number().int().nonnegative(),
        preserved: z.number().int().nonnegative(),
    })
        .strict()),
})
    .strict();
export const mailRematchApplySchema = z
    .object({ snapshotHash: z.string().regex(/^[a-f0-9]{64}$/u) })
    .strict();
//# sourceMappingURL=mail-assignment.js.map